import React, { useEffect, useState, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import Layout from "@components/wrappers/layouts/main-layout/Layout";
import {
    IRevstackFilterOption,
    DASHBOARD_TYPES,
    CA_DD_MODULES,
    SubModule,
    IFiltersFromUrlState,
    IRelativeFiltersState,
    IMainFilterState,
    IRevstackFiltersLayoutProps,
    IFiltersLayoutRootState,
    ITagTypeOption,
    ICubeQuery,
    IDashboardWidgetConfig,
    RevstackDashboardSubModules,
} from "@ts/common-types";
import { getRevstackMainFilters, getSkuChannelMapping } from "@ts/retrieve-data";
import RevstackFiltersSubheader from "@components/widgets/filter-widgets/RevstackFiltersSubheader";
import AgGridLoaderComponent from "@components/loaders/AgGridLoaderComponent";
import { showToast } from "@ts/modals";
import { formatMainFilters } from "@ts/revstack/formatters";
import {
    resetRevstackDashboardWidgetData,
    resetMainFiltersData,
    updateAiInsightsPayloadData,
    updateRevstackDashboardWidgetData,
    updateMainFilters,
    updateMainFiltersData,
    updateSkuMainFilters,
    resetMainFilters,
    refreshRevstackLayoutFiltersLabels
} from "@features/revstackWidgetsSlice";
import {
    useGetDashboardConfig,
    usePreviousValue,
    useRevstackStreamData,
} from "@hooks/AllCustomHooks";
import { filter, map } from "underscore";
import { getActiveModule, getCountryCode } from "@ts/utils";
import { setAdvancedFilters } from "@ts/revstack/helpers";
import {
    hasFilterHashInUrl,
    removeHashFromUrl,
    applyHashFiltersToState
} from "@ts/revstack/filters";
import { REVSTACK_MULTI_CHANNEL_MODULES } from "@/ts/constants";
import { updateRevstackChannel } from "@/features/channelSlice";

const RevstackFiltersLayout: React.FC<IRevstackFiltersLayoutProps> = ({ children, showCategoryFilters = true }) => {
    const dispatch = useDispatch();
    const activeClientId = useSelector(
        (state: IFiltersLayoutRootState) => state.activeClientId.value
    );
    const revstackChannel = useSelector(
        (state: IFiltersLayoutRootState) => state.revstackChannel.value
    );
    const revstackChannelUpdateId = useSelector(
        (state: IFiltersLayoutRootState) => state.revstackChannel.updateId
    );

    const mainFilters = useSelector((state: IFiltersLayoutRootState) => state.mainFilters.value)
    const revstackLayoutFilters = useSelector(
        (state: IFiltersLayoutRootState) => state.revstackLayoutFilters.value
    );
    const selectedDateRange = useSelector(
        (state: IFiltersLayoutRootState) => state.selectedDateRange.value
    );

    const revstackChannelsList = useSelector(
        (state: IFiltersLayoutRootState) => state.revstackChannelsList.value
    );

    const filtersFromUrl = useSelector(
        (state: IFiltersFromUrlState) => state.filtersFromUrl
    );

    const tagTypeListLoading = useSelector(
        (state: { tagTypeListLoading: { value: boolean } }) =>
            state.tagTypeListLoading.value
    );

    const countryCode = getCountryCode();

    //TODO: We can reduce these states to a single reducer state
    const [filtersLoading, setFiltersLoading] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [isBaseRequestFulfilled, setIsBaseRequestFulfilled] = useState(false);
    const additionalFilters = useSelector((state: IFiltersLayoutRootState) => state.additionalFilters?.value || {
        tagTypes: [] as ITagTypeOption[],
        state: {
            isOpen: false,
            appliedFilters: {} as Record<string, string[]>,
            draftFilters: {} as Record<string, string[]>,
            activeIndex: 0,
            searchText: "",
        }
    });
    const additionalFiltersState = additionalFilters.state;
    const pendingClientChangeOrFirstLoadRef = useRef(false);
    const hasInitialFilterFetchCompleted = useRef(false);
    const hasAppliedUrlHashFiltersRef = useRef(false);
    const isProcessingModuleSwitchFromPARef = useRef(false);
    const isApplyingHashFiltersRef = useRef(false);
    const hashFiltersAppliedSuccessfullyRef = useRef(false);
    const hasAdvancedFiltersMetadataLoadedRef = useRef(false);
    const lastProcessedSkuRef = useRef<string | null>(null);

    const activeModule = getActiveModule().activeModule;
    const prevActiveModule = usePreviousValue(activeModule);
    const prevMainFilters = usePreviousValue(mainFilters);
    const prevRevstackLayoutFilters = usePreviousValue(revstackLayoutFilters);
    const prevSelectedDateRange = usePreviousValue(selectedDateRange);
    const prevActiveClientId = usePreviousValue(activeClientId);
    const prevRevstackChannel = usePreviousValue(revstackChannel);
    const prevRevstackChannelUpdateId = usePreviousValue(revstackChannelUpdateId);
    const prevAdditionalFiltersState = usePreviousValue(additionalFiltersState);
    const relativeFilters = useSelector((state: IRelativeFiltersState) => state.relativeFilters.value);
    const sku = relativeFilters?.skus?.[0] || null;
    const prevSku = usePreviousValue(sku);

    // Check if channels are available before proceeding
    const areChannelsAvailable = useMemo(() => {
        return revstackChannelsList && revstackChannelsList.length > 0;
    }, [revstackChannelsList]);

    // Show loading until channels are fetched
    const isChannelsLoading = !areChannelsAvailable;

    /**
     * !Note: Tabs when made dynamic from config this also have to be accessed from there
     * ! Ignoring enums for it now...
     */
    //TODO: this have to replaced by revstackDashboardTabs keys and for that,
    //TODO: we have refactor the `useGetDashboardConfig` hook to compute the config for all the tab at once
    const caTabs = ["overview", "traffic", "conversion", "operations"];
    const overviewCubeQueries: ICubeQuery[][] = [];
    const otherTabsCubeQueries: ICubeQuery[][] = [];
    for (const tab of caTabs) {
        const dashboardConfig = useGetDashboardConfig(
            activeModule,
            DASHBOARD_TYPES.BASE,
            tab as RevstackDashboardSubModules,
            undefined,
            true
        ).widgetConfig as IDashboardWidgetConfig[];

        if (tab === CA_DD_MODULES.OVERVIEW) {
            overviewCubeQueries.push(
                ...map(dashboardConfig, (config: IDashboardWidgetConfig) => config.cubeQueries)
            );
        } else {
            otherTabsCubeQueries.push(
                ...map(dashboardConfig, (config: IDashboardWidgetConfig) => config.cubeQueries)
            );
        }
    }

    const { getRevstackWidgetStreamData } = useRevstackStreamData({
        overviewQueries: overviewCubeQueries,
        otherTabsQueries: otherTabsCubeQueries,
        streamConfig: {
            resetWidgetDataAction: resetRevstackDashboardWidgetData,
            updateWidgetDataAction: updateRevstackDashboardWidgetData,
            updateAiInsightsAction: updateAiInsightsPayloadData,
            aiInsightsKey: "main",
        },
        overviewTabKey: CA_DD_MODULES.OVERVIEW,
    });

    // Determine if filters have changed (excluding channel/client changes)
    const filtersHaveChanged = useMemo(() => {
        if (
            prevActiveClientId === undefined &&
            prevRevstackChannel === undefined
        ) {
            return false;
        }

        const selectedDateRangeChanged =
            JSON.stringify(selectedDateRange) !==
            JSON.stringify(prevSelectedDateRange);
        const mainFiltersChanged =
            JSON.stringify(mainFilters) !== JSON.stringify(prevMainFilters);
        const revstackLayoutFiltersChanged =
            JSON.stringify(revstackLayoutFilters) !==
            JSON.stringify(prevRevstackLayoutFilters);
        const additionalFiltersChanged =
            JSON.stringify(additionalFiltersState?.appliedFilters) !==
            JSON.stringify(prevAdditionalFiltersState?.appliedFilters);
        return (
            selectedDateRangeChanged ||
            mainFiltersChanged ||
            revstackLayoutFiltersChanged ||
            additionalFiltersChanged
        );
    }, [
        selectedDateRange,
        prevSelectedDateRange,
        mainFilters,
        prevMainFilters,
        revstackLayoutFilters,
        prevRevstackLayoutFilters,
        additionalFiltersState?.appliedFilters,
        prevAdditionalFiltersState?.appliedFilters,
        prevActiveClientId,
        prevRevstackChannel,
    ]);

    const activeModuleHasChanged = useMemo(() => {
        if (prevActiveModule === undefined) return false;
        return activeModule !== prevActiveModule;
    }, [activeModule, prevActiveModule]);

    useEffect(() => {
        if (!areChannelsAvailable || !revstackChannelsList || !revstackChannelsList.length) return;

        const isMultiChannelModule = REVSTACK_MULTI_CHANNEL_MODULES.includes(activeModule as SubModule);

        if (!isMultiChannelModule) {
            // Case 1: Multiple channels selected in single-channel mode -> Limit to 1
            if (revstackChannel && revstackChannel.length > 1) {
                dispatch(updateRevstackChannel([revstackChannel[0]]));
            }
            // Case 2: No channels selected -> Select first available
            else if (!revstackChannel || revstackChannel.length === 0) {
                dispatch(updateRevstackChannel([revstackChannelsList[0].channel_id]));
            }
        }
    }, [activeModule, revstackChannel, revstackChannelsList, areChannelsAvailable, dispatch]);

    // Module switching and channel stabilization logic
    const moduleSwitchState = useMemo(() => {
        const isModuleSwitchInProgress =
            prevActiveModule !== undefined && activeModule !== prevActiveModule;

        // If channels are not available, don't proceed
        if (!areChannelsAvailable) {
            return { isModuleSwitchInProgress, areChannelsStabilized: false };
        }

        if (!revstackChannel || revstackChannel.length === 0) {
            return { isModuleSwitchInProgress, areChannelsStabilized: false };
        }

        const isMultiChannelModule = REVSTACK_MULTI_CHANNEL_MODULES.includes(
            activeModule as SubModule
        );
        const areChannelsStabilized = isMultiChannelModule
            ? revstackChannel.length > 1 // Multi-channel: need multiple channels
            : revstackChannel.length === 1; // Single-channel: need exactly one channel

        return { isModuleSwitchInProgress, areChannelsStabilized };
    }, [activeModule, prevActiveModule, revstackChannel, areChannelsAvailable]);


    useEffect(() => {
        dispatch(refreshRevstackLayoutFiltersLabels(countryCode));
    }, [dispatch, countryCode]);

    useEffect(() => {
        if (hasFilterHashInUrl()) {
            hasAppliedUrlHashFiltersRef.current = true;
        }
    }, []);

    /**
     * Track client changes
     * Only tracks changes AFTER initial filter fetch to avoid treating
     * automatic client setup (e.g., from URL hash) as a user-initiated change
     */
    useEffect(() => {
        const clientChanged =
            prevActiveClientId !== undefined &&
            activeClientId !== prevActiveClientId;

        if (clientChanged && hasInitialFilterFetchCompleted.current) {
            pendingClientChangeOrFirstLoadRef.current = true;
            hasAppliedUrlHashFiltersRef.current = false;
            hashFiltersAppliedSuccessfullyRef.current = false;
            hasAdvancedFiltersMetadataLoadedRef.current = false;

            dispatch(refreshRevstackLayoutFiltersLabels(countryCode));
        }
    }, [activeClientId, prevActiveClientId, dispatch]);

    //!Note[DND]: Don't remove comments here...
    // Determine when to trigger widget data request
    const shouldTriggerWidgetRequest = useMemo(() => {
        // Don't trigger widget requests for Product Analysis - handled by RevstackRelativeFiltersLayout
        if (activeModule === SubModule.PRODUCT_ANALYSIS && !sku) {
            return false;
        }

        // Don't trigger if filters are still loading
        if (filtersLoading) return false;

        // Don't trigger if channels are not available yet
        if (!areChannelsAvailable) return false;

        // Don't trigger if we're in the middle of a module switch and channels aren't stabilized
        if (
            moduleSwitchState.isModuleSwitchInProgress &&
            !moduleSwitchState.areChannelsStabilized
        )
            return false;

        // Check if advanced filters from URL need their metadata to be loaded
        // Only block if: URL has hash + filters loaded from URL have advanced filters + metadata not loaded
        const hasAppliedAdvancedFilters = additionalFiltersState?.appliedFilters && 
            Object.keys(additionalFiltersState.appliedFilters).length > 0;
        const hasUrlHash = hasFilterHashInUrl();
        const urlFiltersHaveAdvancedFilters = hasUrlHash && 
            filtersFromUrl.value?.advancedFilters && 
            Object.keys(filtersFromUrl.value.advancedFilters).length > 0;
        
        // If URL has advanced filters but metadata has not been loaded yet, wait
        // This ensures that the tagTypes metadata is available for generateCubeFilters
        if (urlFiltersHaveAdvancedFilters && hasAppliedAdvancedFilters && !hasAdvancedFiltersMetadataLoadedRef.current) {
            return false;
        }

        // Trigger after filters finish loading (for channel/client changes)
        if (isBaseRequestFulfilled) return true;

        // Trigger for filter changes (only after initial load is complete)
        if (filtersHaveChanged && hasInitialFilterFetchCompleted.current) return true;

        // Trigger when active module changes (but only after channels are stabilized)
        // BUT skip if switching FROM Product Analysis - wait for filter fetch to complete instead
        if (activeModuleHasChanged && moduleSwitchState.areChannelsStabilized) {
            const isSwitchingFromProductAnalysis = prevActiveModule === SubModule.PRODUCT_ANALYSIS;
            if (!isSwitchingFromProductAnalysis) {
                return true;
            }
        }

        return false;
    }, [
        activeModule,
        filtersLoading,
        isBaseRequestFulfilled,
        filtersHaveChanged,
        activeModuleHasChanged,
        moduleSwitchState,
        areChannelsAvailable,
        prevActiveModule,
        additionalFiltersState?.appliedFilters,
        additionalFilters?.tagTypes,
        filtersFromUrl.value?.advancedFilters,
        hasAdvancedFiltersMetadataLoadedRef.current,
        sku,
    ]);

    /**
     * Fetch filters when channel selection changes
     * - Initial load: only applies defaults if filters not already set (e.g., from hash)
     * - Client change: applies default filters for new client
     * - Channel change: validates existing filters against new channel
     * - Module switch: fetches filters when switching from Product Analysis to other modules
     */
    useEffect(() => {
        if (!areChannelsAvailable) {
            return;
        }

        const channelStateChanged =
            prevRevstackChannelUpdateId !== undefined &&
            revstackChannelUpdateId !== prevRevstackChannelUpdateId;
        
        // Check if this is just a channel stabilization during initial load
        // The stabilization effect (lines 217-232) can trigger 2-3 times as it adjusts channel count
        // We only want to skip if: channels haven't actually changed AND we've already fetched once
        const isChannelStabilization = 
            hasInitialFilterFetchCompleted.current &&
            prevRevstackChannel &&
            revstackChannel &&
            prevRevstackChannel.length > 0 &&
            revstackChannel.length > 0 &&
            prevRevstackChannel.length === revstackChannel.length &&
            prevRevstackChannel[0] === revstackChannel[0];
        
        // Skip if this is a stabilization after initial fetch (same channels, just updateId changed)
        if (channelStateChanged && isChannelStabilization) {
            return;
        }

        // Check if SKU changed (for Product Analysis module)
        const skuChanged =
            activeModule === SubModule.PRODUCT_ANALYSIS &&
            prevSku !== undefined &&
            sku !== prevSku &&
            sku !== lastProcessedSkuRef.current;

        // Check if client changed
        const clientChanged =
            prevActiveClientId !== undefined &&
            activeClientId !== prevActiveClientId;

        // Check if switching FROM Product Analysis TO another module
        const isSwitchingFromProductAnalysis =
            prevActiveModule === SubModule.PRODUCT_ANALYSIS &&
            activeModule !== SubModule.PRODUCT_ANALYSIS &&
            prevActiveModule !== undefined;

        // Set flag when starting module switch from Product Analysis
        if (isSwitchingFromProductAnalysis) {
            isProcessingModuleSwitchFromPARef.current = true;
        }

        // If we're in the middle of processing a module switch from Product Analysis,
        // and this effect is triggered by a channel change (not the module switch itself),
        // skip it to prevent double-fetch
        if (isProcessingModuleSwitchFromPARef.current && channelStateChanged && !isSwitchingFromProductAnalysis) {
            // Skip this channel-triggered fetch - the module switch will handle it
            return;
        }

        // Allow fetch to proceed if any of these conditions are true:
        // - Client changed
        // - Channel changed
        // - SKU changed (for Product Analysis)
        // - Switching from Product Analysis to another module
        if (!clientChanged && !channelStateChanged && !skuChanged && !isSwitchingFromProductAnalysis) {
            return;
        }
        // Skip if we're currently applying hash filters (to prevent re-trigger)
        if (isApplyingHashFiltersRef.current) {
            return;
        }

        const fetchFilters = async () => {
            setFiltersLoading(true);
            setIsBaseRequestFulfilled(true);
            // Reset advanced filters metadata flag when fetching new filters
            hasAdvancedFiltersMetadataLoadedRef.current = false;
            try {
                setExpanded(false);

                const shouldApplyUrlHashFilters = hasAppliedUrlHashFiltersRef.current;

                // Check for pending SKU from URL filters if we're loading from hash
                // This handles the race condition where the hash is loaded but state isn't updated yet
                const pendingSkuFromUrl = shouldApplyUrlHashFilters && (filtersFromUrl.value as any)?.skus?.[0];
                const effectiveSku = sku || pendingSkuFromUrl;

                const isProductAnalysisWithSku = activeModule === SubModule.PRODUCT_ANALYSIS && effectiveSku;

                let formattedData = null;
                let skuMappingData = null;

                // For Product Analysis with SKU, skip fetching full filter options
                // We only need SKU mapping and will preserve mainFilters from URL hash
                if (isProductAnalysisWithSku) {
                    const skuMapping = await getSkuChannelMapping(effectiveSku, revstackChannel[0]);

                    if (skuMapping && skuMapping.length > 0) {
                        const mapping = skuMapping[0];
                        lastProcessedSkuRef.current = effectiveSku;
                        skuMappingData = {
                            categories: mapping.category_id ? [mapping.category_id] : [],
                            subcategories: mapping.subcategory_id ? [mapping.subcategory_id] : [],
                            brands: mapping.brand_id ? [mapping.brand_id] : [],
                            cross_channel_sku_id: mapping.cross_channel_product_id ? [parseInt(mapping.cross_channel_product_id)] : null,
                        };
                    } else {
                        throw new Error('No SKU mapping found');
                    }
                } else {
                    // For other modules, fetch the full main filters to get available options
                    const res = await getRevstackMainFilters();
                    if (!res.status) throw new Error(res.message);

                    formattedData = formatMainFilters(
                        JSON.parse(res.data)?.category_list
                    );

                    dispatch(updateMainFiltersData(formattedData));
                }
                
                const isClientChange = pendingClientChangeOrFirstLoadRef.current;

                // Mark that at least one filter fetch has completed
                if (!hasInitialFilterFetchCompleted.current) {
                    hasInitialFilterFetchCompleted.current = true;
                }

                // Priority 1: Client change - Always use defaults
                if (isClientChange) {
                    if (isProductAnalysisWithSku) {
                        // For Product Analysis with SKU, only update skuMainFilters
                        // mainFilters will be preserved from URL hash
                        if (skuMappingData) {
                            dispatch(updateSkuMainFilters({
                                selected: {
                                    category: skuMappingData.categories,
                                    subcategory: skuMappingData.subcategories,
                                    brand: skuMappingData.brands,
                                    cross_channel_sku_id: skuMappingData.cross_channel_sku_id,
                                }
                            }));
                        }
                    } else {
                        // For other modules, update mainFilters with full filter defaults
                        dispatch(updateMainFilters(formattedData.selected));
                        // Fetch advanced filters after mainFilters are populated
                        if (formattedData.selected && (formattedData.selected.categories?.length || formattedData.selected.subcategories?.length || formattedData.selected.brands?.length)) {
                            await setAdvancedFilters(
                                formattedData.selected.categories,
                                formattedData.selected.subcategories,
                                formattedData.selected.brands
                            );
                            
                            hasAdvancedFiltersMetadataLoadedRef.current = true;
                        }
                    }

                    pendingClientChangeOrFirstLoadRef.current = false;
                }
                // Priority 2: URL hash filters (first load or reload)
                else if (shouldApplyUrlHashFilters) {
                    // Always apply hash filters first, then handle module-specific logic
                    let hasAppliedHashFilters = false;

                    if (hasFilterHashInUrl()) {
                        if (filtersFromUrl.value && !filtersFromUrl.isLoading) {
                            isApplyingHashFiltersRef.current = true;
                            applyHashFiltersToState(
                                filtersFromUrl.value,
                                { revstackLayoutFilters, selectedDateRange }
                            );
                            hasAppliedHashFilters = true;
                            hashFiltersAppliedSuccessfullyRef.current = true;
                            isApplyingHashFiltersRef.current = false;

                        } else if (filtersFromUrl.isLoading) {
                            // Filters are still loading, wait for next render
                            return;
                        } else if (filtersFromUrl.error) {
                            console.error('Error loading filters from URL:', filtersFromUrl.error);
                            const { activeSection: platform, activeModule: module } = getActiveModule();
                            removeHashFromUrl(platform, module);
                        } else {
                            // No filters loaded yet, wait for next render
                            return;
                        }
                    }

                    // Now handle module-specific logic after hash filters are applied
                    if (isProductAnalysisWithSku) {
                        // For Product Analysis with SKU, update skuMainFilters with SKU mapping
                        if (skuMappingData) {
                            dispatch(updateSkuMainFilters({
                                selected: {
                                    category: skuMappingData.categories,
                                    subcategory: skuMappingData.subcategories,
                                    brand: skuMappingData.brands,
                                    cross_channel_sku_id: skuMappingData.cross_channel_sku_id,
                                }
                            }));
                        }
                    } else {
                        // For other modules, apply default filters if hash filters weren't applied
                        if (!hasAppliedHashFilters) {
                            dispatch(updateMainFilters(formattedData.selected));
                        }
                        // Fetch advanced filters after mainFilters are populated
                        // Check if mainFilters are initialized (either from hash or defaults)
                        const currentMainFilters = hasAppliedHashFilters ? filtersFromUrl.value : formattedData.selected;
                        if (currentMainFilters && (currentMainFilters.categories?.length || currentMainFilters.subcategories?.length || currentMainFilters.brands?.length)) {
                            await setAdvancedFilters(
                                currentMainFilters.categories,
                                currentMainFilters.subcategories,
                                currentMainFilters.brands
                            );
                            
                            hasAdvancedFiltersMetadataLoadedRef.current = true;
                        }
                    }

                    hasAppliedUrlHashFiltersRef.current = false;
                }
                // Priority 3: Channel change - validate and preserve existing filters
                else if (
                    prevRevstackChannel &&
                    JSON.stringify(revstackChannel) !== JSON.stringify(prevRevstackChannel) &&
                    !moduleSwitchState.isModuleSwitchInProgress
                ) {
                    if (isProductAnalysisWithSku) {
                        // For Product Analysis with SKU, only update skuMainFilters on channel change
                        // mainFilters will be preserved from URL hash
                        if (skuMappingData) {
                            dispatch(updateSkuMainFilters({
                                selected: {
                                    category: skuMappingData.categories,
                                    subcategory: skuMappingData.subcategories,
                                    brand: skuMappingData.brands,
                                    cross_channel_sku_id: skuMappingData.cross_channel_sku_id,
                                }
                            }));
                        }
                    } else {
                        // For other modules, validate and update mainFilters
                        const availableCategoryIds = new Set(
                            formattedData.categories.map((c: { value: number }) => c.value)
                        );

                        const getCategorySubcategories = (categoryId: number) =>
                            map(
                                filter(
                                    formattedData.subcategories,
                                    (sub: IRevstackFilterOption) => sub.category === categoryId
                                ),
                                "value"
                            );

                        // Validate existing categories against new channel's available categories
                        const validCategoriesFromMainFilters = mainFilters.categories.filter((cat: number) =>
                            availableCategoryIds.has(cat)
                        );
                        const defaultCategories = formattedData.selected.categories.filter(
                            (cat: number) => availableCategoryIds.has(cat)
                        );
                        const finalCategories =
                            validCategoriesFromMainFilters.length > 0
                                ? validCategoriesFromMainFilters
                                : defaultCategories;

                        // Derive valid subcategories based on final categories
                        const validSubcategorySet = new Set(
                            finalCategories.flatMap(getCategorySubcategories)
                        );

                        const validSubcategoriesFromMainFilters = mainFilters.subcategories.filter(
                            (sub: number) => validSubcategorySet.has(sub)
                        );

                        const defaultSubcategories = formattedData.selected.subcategories.filter(
                            (sub: number) => validSubcategorySet.has(sub)
                        );

                        const finalSubcategories = Array.from(
                            new Set([
                                ...validSubcategoriesFromMainFilters,
                                ...defaultSubcategories,
                            ])
                        );

                        // Filter brands whose subcategories intersect with finalSubcategories
                        const validBrandsFromMainFilters = mainFilters.brands.filter((brandId: number) => {
                            const brand = formattedData.brands.find(
                                (b: IRevstackFilterOption) => b.value === brandId
                            );
                            return brand?.subcategories?.some((scId: number) =>
                                finalSubcategories.includes(scId)
                            );
                        });

                        const defaultBrands = formattedData.selected.brands.filter((brandId: number) => {
                            const brand = formattedData.brands.find(
                                (b: IRevstackFilterOption) => b.value === brandId
                            );
                            return brand?.subcategories?.some((scId: number) =>
                                finalSubcategories.includes(scId)
                            );
                        });

                        const finalBrands = Array.from(
                            new Set([...validBrandsFromMainFilters, ...defaultBrands])
                        );

                        dispatch(
                            updateMainFilters({
                                categories: finalCategories,
                                subcategories: finalSubcategories,
                                brands: finalBrands,
                                rerender: !mainFilters.rerender,
                            })
                        );

                        // Fetch advanced filters after mainFilters are populated
                        if (finalCategories?.length || finalSubcategories?.length || finalBrands?.length) {
                            await setAdvancedFilters(
                                finalCategories,
                                finalSubcategories,
                                finalBrands
                            );
                            
                            hasAdvancedFiltersMetadataLoadedRef.current = true;
                        }

                    }
                }
                // Priority 4: Default - use API defaults
                else {
                    if (isProductAnalysisWithSku) {
                        // For Product Analysis with SKU, only update skuMainFilters
                        // mainFilters will be preserved from URL hash
                        if (skuMappingData) {
                            dispatch(updateSkuMainFilters({
                                selected: {
                                    category: skuMappingData.categories,
                                    subcategory: skuMappingData.subcategories,
                                    brand: skuMappingData.brands,
                                    cross_channel_sku_id: skuMappingData.cross_channel_sku_id,
                                }
                            }));
                        }
                    } else {
                        if (!hashFiltersAppliedSuccessfullyRef.current) {
                            dispatch(updateMainFilters(formattedData.selected));
                            // Fetch advanced filters after mainFilters are populated
                            if (formattedData.selected && (formattedData.selected.categories?.length || formattedData.selected.subcategories?.length || formattedData.selected.brands?.length)) {
                                await setAdvancedFilters(
                                    formattedData.selected.categories,
                                    formattedData.selected.subcategories,
                                    formattedData.selected.brands
                                );
                                
                                hasAdvancedFiltersMetadataLoadedRef.current = true;
                            }
                        }
                    }
                }
            } catch (error) {
                const errorMessage = (error as Error).message;
                console.error(errorMessage);
                showToast("danger", errorMessage);
                dispatch(resetMainFiltersData());
                dispatch(resetMainFilters())
            } finally {
                setFiltersLoading(false);
                // Reset the module switch flag after filter fetch completes
                isProcessingModuleSwitchFromPARef.current = false;
            }
        };
        fetchFilters();
    }, [
        areChannelsAvailable,
        revstackChannelUpdateId,
        activeModule,
        sku,
        filtersFromUrl,
        activeClientId
    ]);

    useEffect(() => {
        if (shouldTriggerWidgetRequest) {
            getRevstackWidgetStreamData();
            if (isBaseRequestFulfilled) {
                setIsBaseRequestFulfilled(false);
            }
        }
    }, [shouldTriggerWidgetRequest, isBaseRequestFulfilled]);


    // Combined loading state
    // @Sarvesh Need to add a loading state for the filter to disabled later
    const isLoading = useMemo(() => {
        return filtersLoading || isChannelsLoading
    }, [filtersLoading, isChannelsLoading]);

    return (
        <>
            <Layout>
                <div className="d-flex flex-column h-100 revstack-dashboard-scope">
                    {
                        showCategoryFilters && (
                        <RevstackFiltersSubheader
                            isLoading={isLoading}
                            expanded={expanded}
                            setExpanded={setExpanded}
                            tagTypeListLoading={tagTypeListLoading}
                            additionalFiltersState={additionalFiltersState as unknown as IMainFilterState}
                        />

                        )
                    }
                    <div
                        className="revstack-layout-body flex-grow-1"
                        style={{ height: `calc(100vh - ${showCategoryFilters ? 180 : 88}px)` }}
                    >
                        {isLoading ? (
                            <div className="d-flex align-items-center justify-content-center h-100">
                                <span
                                    style={{
                                        transform: "translate(-10%)",
                                        marginBottom: "5%",
                                    }}
                                >
                                    <AgGridLoaderComponent />
                                </span>
                            </div>
                        ) : (
                            children
                        )}
                    </div>
                </div>
            </Layout>
        </>
    );
};

export default RevstackFiltersLayout;
